import { Money, Weight } from '@rematter/frontend-lib-api-client';
import { toastQueue } from '@rematter/ui-web';
import { atom, Getter, PrimitiveAtom, Setter, WritableAtom } from 'jotai';
import isEqual from 'lodash/isEqual';

import {
  $itemsInterfaceEntries,
  $itemsInterfaceErrorItemIds,
  $itemsInterfaceTicketId,
} from '../_components/_shared/ItemsInterface/_state';
import {
  enumLineItemKind,
  ItemsListEntry,
  NormalizedDeduction,
  NormalizedLineItem,
} from '../_components/_shared/ItemsInterface/_types';

import { mapWith, mapWithout } from './utils/mapAtomHelpers';

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

type ExtractedMutationResult = {
  lineItems: Array<{
    id: string;
    type: string;
    itemId: string | null;
    tagId: string | null;
  }>;
  deductions: Array<{
    id: string;
    deductionTypeId: string;
    applicationType: string;
    calculatedAmount?: { amount: number; currencyCode: string } | null;
    weightValue?: { value: number; unit: string } | null;
    priceValue?: { amount: number; currencyCode: string } | null;
    percentage?: number | null;
    purchaseLoadLineItemId?: string | null;
    notes?: string | null;
  }>;
};

export type MutationLifecycle = {
  onBefore: (get: Getter, set: Setter, ticketId: string) => void;
  onSuccess: (
    get: Getter,
    set: Setter,
    ticketId: string,
    sentLineItems: Array<{ id?: string | null }> | undefined,
    sentDeductions: Array<{ id?: unknown }> | undefined,
    result: ExtractedMutationResult,
    failedToSaveMessage: ((label: string) => string) | undefined,
  ) => void;
  onError: (
    get: Getter,
    set: Setter,
    ticketId: string,
    failedToSaveMessage: ((label: string) => string) | undefined,
  ) => void;
};

type BuildMutationParamsCtx = {
  ticketId: string;
  failedToSaveMessage: ((label: string) => string) | undefined;
  entries: ItemsListEntry[];
  tempToRealMap: Map<string, string>;
  weights:
    | { heavyWeight: Weight | null; lightWeight: Weight | null }
    | undefined;
};

export type FreightData = {
  freightCost: Money | null;
  freightTerms: string | null;
};

type MaterialsActionConfig<TLineItem, TDeduction> = {
  debugPrefix: string;

  normalizeEntries: (
    lineItems: TLineItem[],
    deductions: TDeduction[],
    freight?: FreightData | null,
  ) => ItemsListEntry[];

  setupMutationAtom: (
    lifecycle: MutationLifecycle,
  ) => WritableAtom<unknown, [BuildMutationParamsCtx], void>;
};

export type SaveMaterialsParams = {
  ticketId: string;
  immediate?: boolean;
  fromRetry?: boolean;
  failedToSaveMessage?: (ticketLabel: string) => string;
};

export type SyncParams<TLineItem, TDeduction> = {
  ticketId: string;
  documentNumber?: number | string;
  serverUpdatedAt?: string;
  lineItems: TLineItem[];
  deductions: TDeduction[];
  freight?: FreightData | null;
};

export type MaterialsActions<TLineItem, TDeduction> = {
  $save: WritableAtom<null, [SaveMaterialsParams], void>;
  $sync: WritableAtom<null, [SyncParams<TLineItem, TDeduction>], void>;
};

// ──────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ──────────────────────────────────────────────────────────────────────────────

export const resolveId = (
  id: string,
  tempToRealMap: Map<string, string>,
): string | undefined => (id.startsWith('temp-') ? tempToRealMap.get(id) : id);

export const detectFailedItemIds = (
  localEntries: ItemsListEntry[],
  baselineEntries: ItemsListEntry[],
  tempToRealMap: Map<string, string>,
): Set<string> => {
  const failedIds = new Set<string>();
  const baselineById = new Map(baselineEntries.map((e) => [e.id, e]));

  for (const localEntry of localEntries) {
    const resolvedId = localEntry.id.startsWith('temp-')
      ? tempToRealMap.get(localEntry.id)
      : localEntry.id;

    if (!resolvedId) {
      failedIds.add(localEntry.id);
      continue;
    }

    const baselineEntry = baselineById.get(resolvedId);
    if (!baselineEntry) {
      failedIds.add(localEntry.id);
      continue;
    }

    const comparableLocal = { ...localEntry, id: resolvedId };
    if (!isEqual(comparableLocal, baselineEntry)) {
      failedIds.add(localEntry.id);
    }
  }

  return failedIds;
};

// ──────────────────────────────────────────────────────────────────────────────
// Shared state atoms (single instances, keyed by ticketId)
// ──────────────────────────────────────────────────────────────────────────────

const MAX_AUTO_SAVE_RETRIES = 3;

// 🔵🔵🔵 LOCAL STATE - Whether local entries differ from baseline (unsaved changes)
export const $materialsDirtyById: PrimitiveAtom<Map<string, boolean>> = atom(
  new Map(),
);
$materialsDirtyById.debugLabel = '$materialsDirtyById';

// 🔵🔵🔵 LOCAL STATE - Current local line item entries keyed by ticket ID
export const $materialsLocalEntriesById: PrimitiveAtom<
  Map<string, ItemsListEntry[]>
> = atom(new Map());
$materialsLocalEntriesById.debugLabel = '$materialsLocalEntriesById';

// 🔵🔵🔵 LOCAL STATE - In-flight snapshot taken before a save request (for rollback on error)
export const $materialsSnapshotById: PrimitiveAtom<
  Map<string, ItemsListEntry[] | null>
> = atom(new Map());
$materialsSnapshotById.debugLabel = '$materialsSnapshotById';

// 🔵🔵🔵 LOCAL STATE - Maps temp IDs to real server IDs after creation
export const $materialsTempToRealIdMapById: PrimitiveAtom<
  Map<string, Map<string, string>>
> = atom(new Map());
$materialsTempToRealIdMapById.debugLabel = '$materialsTempToRealIdMapById';

// 🔵🔵🔵 LOCAL STATE - Last saved server snapshot (used to detect unsaved changes)
export const $materialsBaselineById: PrimitiveAtom<
  Map<string, ItemsListEntry[] | null>
> = atom(new Map());
$materialsBaselineById.debugLabel = '$materialsBaselineById';

// 🔵🔵🔵 LOCAL STATE - Timestamp of last successful server sync per ticket
export const $materialsLastSyncedUpdatedAtById: PrimitiveAtom<
  Map<string, string | null>
> = atom(new Map());
$materialsLastSyncedUpdatedAtById.debugLabel =
  '$materialsLastSyncedUpdatedAtById';

// 🔵🔵🔵 LOCAL STATE - Flags tickets that need a flush once the in-flight save completes
export const $materialsPendingFlushById: PrimitiveAtom<Map<string, boolean>> =
  atom(new Map());
$materialsPendingFlushById.debugLabel = '$materialsPendingFlushById';

// 🔵🔵🔵 LOCAL STATE - Auto-save retry count per ticket
const $materialsAutoSaveRetryCountById: PrimitiveAtom<Map<string, number>> =
  atom(new Map());
$materialsAutoSaveRetryCountById.debugLabel =
  '$materialsAutoSaveRetryCountById';

// 🔵🔵🔵 LOCAL STATE - Document number cache per ticket (for error toast labels)
const $materialsDocumentNumberById: PrimitiveAtom<
  Map<string, number | string>
> = atom(new Map());
$materialsDocumentNumberById.debugLabel = '$materialsDocumentNumberById';

// 🔵🔵🔵 LOCAL STATE - Force next server sync to overwrite local state (set after max retries)
const $materialsForceOverwriteById: PrimitiveAtom<Map<string, boolean>> = atom(
  new Map(),
);
$materialsForceOverwriteById.debugLabel = '$materialsForceOverwriteById';

// 🔵🔵🔵 LOCAL STATE - Pending debounce timers keyed by ticket ID
const $materialsDebounceTimers: PrimitiveAtom<
  Map<string, ReturnType<typeof setTimeout>>
> = atom(new Map());
$materialsDebounceTimers.debugLabel = '$materialsDebounceTimers';

// ──────────────────────────────────────────────────────────────────────────────
// Shared action atoms
// ──────────────────────────────────────────────────────────────────────────────

// 🟠🟠🟠 ACTION - Set local entries for a specific ticket
export const $setMaterialsLocalEntries = atom(
  null,
  (
    _get: Getter,
    set: Setter,
    params: { ticketId: string; entries: ItemsListEntry[] },
  ) => {
    set($materialsLocalEntriesById, (prev) =>
      mapWith(prev, params.ticketId, params.entries),
    );
  },
);
$setMaterialsLocalEntries.debugLabel = '$setMaterialsLocalEntries';

// 🟠🟠🟠 ACTION - Set dirty flag for a specific ticket
export const $setMaterialsDirty = atom(
  null,
  (_get: Getter, set: Setter, params: { ticketId: string; dirty: boolean }) => {
    set($materialsDirtyById, (prev) =>
      mapWith(prev, params.ticketId, params.dirty),
    );
  },
);
$setMaterialsDirty.debugLabel = '$setMaterialsDirty';

// 🟠🟠🟠 ACTION - Cleanup all state for a ticket
export const $cleanupMaterials = atom(
  null,
  (get: Getter, set: Setter, ticketId: string) => {
    const timers = get($materialsDebounceTimers);
    const timer = timers.get(ticketId);
    if (timer) clearTimeout(timer);

    set($materialsDirtyById, (prev) => mapWithout(prev, ticketId));
    set($materialsLocalEntriesById, (prev) => mapWithout(prev, ticketId));
    set($materialsSnapshotById, (prev) => mapWithout(prev, ticketId));
    set($materialsTempToRealIdMapById, (prev) => mapWithout(prev, ticketId));
    set($materialsBaselineById, (prev) => mapWithout(prev, ticketId));
    set($materialsLastSyncedUpdatedAtById, (prev) =>
      mapWithout(prev, ticketId),
    );
    set($materialsPendingFlushById, (prev) => mapWithout(prev, ticketId));
    set($materialsAutoSaveRetryCountById, (prev) => mapWithout(prev, ticketId));
    set($materialsDocumentNumberById, (prev) => mapWithout(prev, ticketId));
    set($materialsForceOverwriteById, (prev) => mapWithout(prev, ticketId));
    set($materialsDebounceTimers, (prev) => mapWithout(prev, ticketId));
  },
);
$cleanupMaterials.debugLabel = '$cleanupMaterials';

// ──────────────────────────────────────────────────────────────────────────────
// Lifecycle handlers (shared logic, used by both inbound and outbound)
// ──────────────────────────────────────────────────────────────────────────────

function createLifecycle(
  $save: WritableAtom<null, [SaveMaterialsParams], void>,
): MutationLifecycle {
  return {
    onBefore: (get, set, ticketId) => {
      const currentEntries =
        get($materialsLocalEntriesById).get(ticketId) ?? [];
      set($materialsSnapshotById, (prev) =>
        mapWith(prev, ticketId, currentEntries),
      );
    },

    onSuccess: (
      get,
      set,
      ticketId,
      sentLineItems,
      sentDeductions,
      extractedResult,
      failedToSaveMessage,
    ) => {
      const hasNewItems = sentLineItems?.some((item) => !item.id) ?? false;
      const hasNewDeductions = sentDeductions?.some((d) => !d.id) ?? false;

      if (hasNewItems || hasNewDeductions) {
        const currentEntries =
          get($materialsLocalEntriesById).get(ticketId) ?? [];
        const currentMap =
          get($materialsTempToRealIdMapById).get(ticketId) ??
          new Map<string, string>();
        const newMap = new Map(currentMap);

        if (hasNewItems) {
          const sentExistingIds = new Set(
            sentLineItems?.filter((item) => item.id).map((item) => item.id),
          );
          const newReturnedItems = extractedResult.lineItems.filter(
            (item) => !sentExistingIds.has(item.id),
          );

          const tempEntries = currentEntries.filter(
            (entry): entry is NormalizedLineItem =>
              entry.kind === enumLineItemKind.LineItem &&
              entry.id.startsWith('temp-'),
          );

          const consumedIndices = new Set<number>();
          for (const tempEntry of tempEntries) {
            if (newMap.has(tempEntry.id)) continue;
            const matchIndex = newReturnedItems.findIndex(
              (ri, idx) =>
                !consumedIndices.has(idx) &&
                ri.type === tempEntry.type &&
                ri.itemId === tempEntry.itemId &&
                ri.tagId === tempEntry.tagId,
            );
            if (matchIndex !== -1) {
              newMap.set(tempEntry.id, newReturnedItems[matchIndex].id);
              consumedIndices.add(matchIndex);
            }
          }
        }

        if (hasNewDeductions) {
          const sentExistingDeductionIds = new Set(
            sentDeductions?.filter((d) => d.id).map((d) => d.id as string),
          );
          const newReturnedDeductions = extractedResult.deductions.filter(
            (d) => !sentExistingDeductionIds.has(d.id),
          );

          const tempDeductions = currentEntries.filter(
            (entry): entry is NormalizedDeduction =>
              entry.kind === enumLineItemKind.Deduction &&
              entry.id.startsWith('temp-'),
          );

          const consumedDeductionIndices = new Set<number>();
          for (const tempEntry of tempDeductions) {
            if (newMap.has(tempEntry.id)) continue;
            const matchIndex = newReturnedDeductions.findIndex(
              (rd, idx) =>
                !consumedDeductionIndices.has(idx) &&
                rd.deductionTypeId === tempEntry.deductionTypeId &&
                rd.applicationType === tempEntry.applicationType,
            );
            if (matchIndex !== -1) {
              newMap.set(tempEntry.id, newReturnedDeductions[matchIndex].id);
              consumedDeductionIndices.add(matchIndex);
            }
          }
        }

        set($materialsTempToRealIdMapById, (prev) =>
          mapWith(prev, ticketId, newMap),
        );
      }

      const savedSnapshot = get($materialsSnapshotById).get(ticketId) ?? null;
      set($materialsSnapshotById, (prev) => mapWith(prev, ticketId, null));

      const hasPendingFlush =
        get($materialsPendingFlushById).get(ticketId) ?? false;

      if (hasPendingFlush) {
        set($materialsBaselineById, (prev) =>
          mapWith(prev, ticketId, savedSnapshot ?? []),
        );
        set($materialsPendingFlushById, (prev) =>
          mapWith(prev, ticketId, false),
        );
        setTimeout(() => {
          set($save, {
            ticketId,
            immediate: true,
            failedToSaveMessage,
          });
        }, 0);
      } else {
        const currentEntries =
          get($materialsLocalEntriesById).get(ticketId) ?? [];
        const hasDiverged = !isEqual(currentEntries, savedSnapshot ?? []);

        set($materialsDirtyById, (prev) =>
          mapWith(prev, ticketId, hasDiverged),
        );
        set($materialsBaselineById, (prev) =>
          mapWith(prev, ticketId, savedSnapshot ?? []),
        );

        if (hasDiverged) {
          setTimeout(() => {
            set($save, {
              ticketId,
              immediate: true,
              failedToSaveMessage,
            });
          }, 0);
        }
      }

      set($materialsAutoSaveRetryCountById, (prev) =>
        mapWithout(prev, ticketId),
      );

      const currentTicketId = get($itemsInterfaceTicketId);
      if (
        currentTicketId === ticketId &&
        get($itemsInterfaceErrorItemIds).size > 0
      ) {
        set($itemsInterfaceErrorItemIds, new Set());
      }
    },

    onError: (get, set, ticketId, failedToSaveMessage) => {
      const snapshot = get($materialsSnapshotById).get(ticketId) ?? null;
      if (snapshot) {
        set($materialsLocalEntriesById, (prev) =>
          mapWith(prev, ticketId, snapshot),
        );
      }
      set($materialsSnapshotById, (prev) => mapWith(prev, ticketId, null));

      const hasPendingFlush =
        get($materialsPendingFlushById).get(ticketId) ?? false;
      if (hasPendingFlush) {
        set($materialsPendingFlushById, (prev) =>
          mapWith(prev, ticketId, false),
        );
        setTimeout(() => {
          set($save, {
            ticketId,
            immediate: true,
            failedToSaveMessage,
          });
        }, 0);
        return;
      }

      const retryCount =
        get($materialsAutoSaveRetryCountById).get(ticketId) ?? 0;
      if (retryCount >= MAX_AUTO_SAVE_RETRIES) {
        const docNumber = get($materialsDocumentNumberById).get(ticketId);
        const ticketLabel = docNumber
          ? `#${String(docNumber).padStart(6, '0')}`
          : ticketId;
        const failedMessage = failedToSaveMessage?.(ticketLabel);
        toastQueue.add(
          {
            title: failedMessage || 'Error',
            variant: 'error',
          },
          { timeout: 8000 },
        );

        const activeTicketId = get($itemsInterfaceTicketId);
        if (activeTicketId === ticketId) {
          const localEntries =
            get($materialsLocalEntriesById).get(ticketId) ?? [];
          const baseline = get($materialsBaselineById).get(ticketId) ?? [];
          const tempToRealMap =
            get($materialsTempToRealIdMapById).get(ticketId) ??
            new Map<string, string>();
          const errorIds = detectFailedItemIds(
            localEntries,
            baseline,
            tempToRealMap,
          );
          set($itemsInterfaceErrorItemIds, errorIds);
        }

        set($materialsForceOverwriteById, (prev) =>
          mapWith(prev, ticketId, true),
        );

        return;
      }
      set($materialsAutoSaveRetryCountById, (prev) =>
        mapWith(prev, ticketId, retryCount + 1),
      );

      setTimeout(() => {
        set($save, {
          ticketId,
          fromRetry: true,
          immediate: true,
          failedToSaveMessage,
        });
      }, 0);
    },
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const preserveLocalOrder = (
  currentForm: ItemsListEntry[],
  snapshot: ItemsListEntry[],
  tempToRealMap: Map<string, string>,
): ItemsListEntry[] => {
  if (currentForm.length === 0) return snapshot;

  const snapshotById = new Map(snapshot.map((e) => [e.id, e]));
  const consumed = new Set<string>();
  const result: ItemsListEntry[] = [];

  for (const localEntry of currentForm) {
    const realId = localEntry.id.startsWith('temp-')
      ? tempToRealMap.get(localEntry.id)
      : localEntry.id;
    const match =
      (realId ? snapshotById.get(realId) : undefined) ??
      snapshotById.get(localEntry.id);

    if (match) {
      result.push(match);
      consumed.add(match.id);
    }
  }

  for (const entry of snapshot) {
    if (!consumed.has(entry.id)) {
      result.push(entry);
    }
  }

  return result;
};

// ──────────────────────────────────────────────────────────────────────────────
// Factory for type-specific action atoms
// ──────────────────────────────────────────────────────────────────────────────

export function createMaterialsActions<TLineItem, TDeduction>(
  config: MaterialsActionConfig<TLineItem, TDeduction>,
): MaterialsActions<TLineItem, TDeduction> {
  const { debugPrefix } = config;

  // 🟠🟠🟠 ACTION - Save materials (debounced by default, immediate when flushing)
  const $save = atom(
    null,
    (get: Getter, set: Setter, params: SaveMaterialsParams) => {
      const { ticketId, immediate, failedToSaveMessage } = params;

      const timers = get($materialsDebounceTimers);
      const prevTimer = timers.get(ticketId);
      if (prevTimer) clearTimeout(prevTimer);

      const isDirty = get($materialsDirtyById).get(ticketId) ?? false;
      if (!isDirty) return;

      if (!params.fromRetry) {
        set($materialsAutoSaveRetryCountById, (prev) =>
          mapWithout(prev, ticketId),
        );
      }

      const executeSave = () => {
        if (!(get($materialsDirtyById).get(ticketId) ?? false)) return;

        const inFlightSnapshot =
          get($materialsSnapshotById).get(ticketId) ?? null;
        if (inFlightSnapshot !== null) {
          set($materialsPendingFlushById, (prev) =>
            mapWith(prev, ticketId, true),
          );
          return;
        }

        const entries = get($materialsLocalEntriesById).get(ticketId) ?? [];
        const tempToRealMap =
          get($materialsTempToRealIdMapById).get(ticketId) ??
          new Map<string, string>();

        const currentTicketId = get($itemsInterfaceTicketId);
        const weights =
          currentTicketId === ticketId
            ? {
                heavyWeight: get($itemsInterfaceEntries).heavyWeight,
                lightWeight: get($itemsInterfaceEntries).lightWeight,
              }
            : undefined;

        set($updateMutation, {
          ticketId,
          failedToSaveMessage,
          entries,
          tempToRealMap,
          weights,
        });
      };

      if (immediate) {
        executeSave();
      } else {
        const newTimer = setTimeout(executeSave, 1000);
        set($materialsDebounceTimers, (prev) =>
          mapWith(prev, ticketId, newTimer),
        );
      }
    },
  );
  $save.debugLabel = `$${debugPrefix}SaveMaterials`;

  const lifecycle = createLifecycle($save);

  // 🟠🟠🟠 ACTION - Mutation dispatch atom (internal, wraps type-specific mutation)
  const $updateMutation = config.setupMutationAtom(lifecycle);
  $updateMutation.debugLabel = `$${debugPrefix}UpdateMutation`;

  // 🟠🟠🟠 ACTION - Sync server data into local form state (cancels pending debounce)
  const $sync = atom(
    null,
    (get: Getter, set: Setter, params: SyncParams<TLineItem, TDeduction>) => {
      if (params.documentNumber != null) {
        const docNum = params.documentNumber;
        set($materialsDocumentNumberById, (prev) =>
          mapWith(prev, params.ticketId, docNum),
        );
      }

      const timers = get($materialsDebounceTimers);
      const prevTimer = timers.get(params.ticketId);
      if (prevTimer) clearTimeout(prevTimer);
      set($materialsDebounceTimers, (prev) =>
        mapWithout(prev, params.ticketId),
      );

      if (params.serverUpdatedAt) {
        const lastSynced =
          get($materialsLastSyncedUpdatedAtById).get(params.ticketId) ?? null;
        if (lastSynced && params.serverUpdatedAt < lastSynced) return;
        set($materialsLastSyncedUpdatedAtById, (prev) =>
          mapWith(prev, params.ticketId, params.serverUpdatedAt ?? null),
        );
      }

      const snapshot = config.normalizeEntries(
        params.lineItems,
        params.deductions,
        params.freight,
      );

      const inFlightSnapshot =
        get($materialsSnapshotById).get(params.ticketId) ?? null;
      if (inFlightSnapshot !== null) {
        set($materialsBaselineById, (prev) =>
          mapWith(prev, params.ticketId, snapshot),
        );
        return;
      }

      const forceOverwrite =
        get($materialsForceOverwriteById).get(params.ticketId) ?? false;
      const currentBaseline =
        get($materialsBaselineById).get(params.ticketId) ?? null;
      const currentForm =
        get($materialsLocalEntriesById).get(params.ticketId) ?? [];
      const tempToRealMap =
        get($materialsTempToRealIdMapById).get(params.ticketId) ??
        new Map<string, string>();
      const orderedSnapshot = preserveLocalOrder(
        currentForm,
        snapshot,
        tempToRealMap,
      );

      if (
        forceOverwrite ||
        currentBaseline === null ||
        isEqual(currentForm, currentBaseline)
      ) {
        set($materialsLocalEntriesById, (prev) =>
          mapWith(prev, params.ticketId, orderedSnapshot),
        );
        set($materialsDirtyById, (prev) =>
          mapWith(prev, params.ticketId, false),
        );
        if (forceOverwrite) {
          const activeTicketId = get($itemsInterfaceTicketId);
          if (activeTicketId === params.ticketId) {
            set($itemsInterfaceErrorItemIds, new Set());
          }

          set($materialsForceOverwriteById, (prev) =>
            mapWithout(prev, params.ticketId),
          );
          set($materialsTempToRealIdMapById, (prev) =>
            mapWithout(prev, params.ticketId),
          );
          set($materialsAutoSaveRetryCountById, (prev) =>
            mapWithout(prev, params.ticketId),
          );
        }
      }
      set($materialsBaselineById, (prev) =>
        mapWith(prev, params.ticketId, orderedSnapshot),
      );
    },
  );
  $sync.debugLabel = `$${debugPrefix}SyncMaterialsForm`;

  return { $save, $sync };
}
